import React, { useContext, useEffect, useMemo } from "react";
import { animated } from "@react-spring/web";
import cx from "classnames";
import { get,uniqBy,orderBy,map,isEqual,times,size} from "lodash";
import dateformat from "dateformat";

import InfiniteScroll from "src/components/infiniteScroll";
import Slider from "src/components/slider";
import Button from "src/components/button";
import Text from "src/components/text";
import { AssetCard as AssetSelectionCard } from "src/components/asset/add/card";
import { AssetPlaceholder } from "src/components/entityBrowser";
import Thumbnail from "src/components/thumbnail";

import { DealRoomActionPlanContext } from "../../context";
import { FileService } from "src/utils/file.service";
import { Asset } from "src/types/assets";
import {assetMetadata} from "src/utils/dealRoom.helper";

import styles from "./styles.module.scss";
const EmptyAssetsImage = "/images/assets/assetsEmptyMessage.svg";

interface AddAssetSliderProps {
  dealRoomId: string;
  show: boolean;
  onFinish: (
    dealRoomId: string,
    actionId: string,
    isNewAction: boolean,
    entities: Asset[],
  ) => void;
  onClose: (options?: { cancelled?: boolean }) => void;
  assets: Asset[];
  fetchAssets: (dealRoomId: string, page: number) => void;
}

const AddAssetSlider: React.FC<AddAssetSliderProps> = ({
  dealRoomId,
  show,
  onFinish,
  onClose,
}) => {
  const { state, handleFetchAssets } = useContext(DealRoomActionPlanContext);

  const stageId = get(state, "addAssetSlider.stageId");
  const actionId = get(state, "addAssetSlider.actionId");
  const existingAssets = get(state, "addAssetSlider.existingAssets", []);
  const isTargetActionNew = get(state, "addAssetSlider.isNewAction");

  const dealRoomAssetsObj = get(state, "assets");
  const dealRoomAssets = get(dealRoomAssetsObj, "items", []);
  const pagination = get(dealRoomAssetsObj, "pagination", {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 20,
    totalRecordsCount: 0,
    hasMoreItems: false,
  });
  const { currentPage, totalPages, hasMoreItems } = pagination;
  const dealRoomAssetsLoading = get(dealRoomAssetsObj, "loading");

  const [entities, setEntities] = React.useState<Asset[]>(existingAssets);

  const hasEntities = entities?.length > 0;

  useEffect(() => {
    setEntities(existingAssets || []);

    if (show && currentPage < 0) {
      handleFetchAssets(dealRoomId, 0);
    }
  }, [show, currentPage]);

  const handleStateChange = (entities: Asset[]) => {
    setEntities(entities);
  };

  const handleLoadAssets = () => {
    const nextPage = currentPage + 1;
    if (nextPage < totalPages) {
      handleFetchAssets(dealRoomId, nextPage);
    }
  };

  const handleClose = () => {
    onClose({ cancelled: true });
  };

  const handleFinish = () => {
    onFinish(stageId, actionId, isTargetActionNew, entities);

    onClose({ cancelled: false });
  };

  const handleSelection = (entity: Asset) => {
    let modifiedEntities = [...entities, entity];
    modifiedEntities = uniqBy(modifiedEntities, "id");
    setEntities(modifiedEntities);
  };

  const hasChanges = useMemo(() => {
    const orderedExistingAssets = map(orderBy(existingAssets, "id"), asset => get(asset, "id"));
    const orderedEntities = map(orderBy(entities, "id"), asset => get(asset, "id"));

    return !isEqual(orderedExistingAssets, orderedEntities);
  }, [entities, existingAssets]);

  return (
    <Slider.Dialog
      show={show}
      width={hasEntities ? "720px" : "380px"}
      className={styles.dialog}
      onClose={handleClose}
    >
      <Slider.Body className={styles.body}>
        <div className={styles.assetPanel}>
          <div className={styles.addAsset}>
            <div className={styles.header}>
              <div className={styles.primary}>
                <div className={styles.row}>
                  <h5 className={styles.title}>adding assets</h5>
                </div>
                <p className={styles.message}>Select assets from your deal room to attach to this action item.</p>
              </div>
            </div>
            <InfiniteScroll
              hasMore={hasMoreItems}
              loading={dealRoomAssetsLoading}
              loadMore={handleLoadAssets}
              loader={() => {
                return (
                  <>
                    {times(4, () => (
                      <AssetPlaceholder />
                    ))}
                  </>
                );
              }}
              className={styles.assetsContainer}
            >
              {dealRoomAssets?.map((asset: Asset, index: number) => {
                return (
                  <AssetCard
                    entity={asset.asset as Asset}
                    onSelection={handleSelection}
                    key={`assetCard-${index}`}
                  />
                );
              })}
            </InfiniteScroll>

            {(size(dealRoomAssets) === 0 && !dealRoomAssetsLoading) && (
              <div className={styles.emptyAssetState}>
                <img className={styles.image} src={EmptyAssetsImage} />
                <p className={styles.subtext}>No assets attached yet!</p>
              </div>
            )}
          </div>

          {hasEntities && (
            <div className={styles.entitySelectionContainer}>
              <AssetSelection
                entities={entities}
                onStateChange={handleStateChange}
              />
            </div>
          )}
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          variant="primary"
          icon="paperflite-tick"
          text="Confirm"
          onClick={handleFinish}
          disabled={!hasChanges}
        />
        <Button variant="secondary" text="Cancel" onClick={handleClose} />
      </Slider.Actions>
    </Slider.Dialog>
  );
};

interface AssetSelectionProps {
  entities: Asset[];
  onStateChange: (state: Asset[]) => void;
}

const AssetSelection: React.FC<AssetSelectionProps> = ({
  entities,
  onStateChange,
}) => {
  const assets = entities || [];

  const handleRemove = (asset: Asset) => {
    const assets = entities.filter((entity) => entity.id !== asset.id);
    onStateChange(assets);
  };

  return (
    <animated.div className={styles.entitySelectionContainer}>
      <div className={styles.entityHeader}>
        <h6 className={styles.title}>{size(assets)} Assets</h6>
      </div>
      <div className={styles.entities}>
        {assets.map((asset) => {
          return (
            <div className={styles.asset} key={asset.id}>
              <AssetSelectionCard
                asset={asset as Asset}
                editable={true}
                onRemove={handleRemove}
              />
            </div>
          );
        })}
      </div>
    </animated.div>
  );
};

interface AssetCardProps {
  entity: Asset;
  onSelection: (entity: Asset) => void;
}

const AssetCard: React.FC<AssetCardProps> = ({ entity, onSelection }) => {
  const fileType = useMemo(() => {
    return FileService.getFileTypeDealRoomAsset(entity.metadata);
  }, [entity]);


  const handleClick = () => {
    if (onSelection) {
      onSelection(entity);
    }
  };

  return (
    <div
      className={cx(styles.entity, styles.asset)}
      style={{ width: 318 }}
      onClick={handleClick}
    >
      <div className={styles.inner}>
        <Thumbnail
          className={styles.icon}
          media={entity?.icon}
          width={68}
          height={80}
          defaultImage="/images/assets/default_asset.jpg"
        />
        <div className={styles.details}>
          <Text
            className={styles.name}
            text={get(entity, "name") ?? "N/A"}
            tooltip={{ placement: "right" }}
          />
          <div className={styles.meta}>
            <p>{assetMetadata(entity)}</p>
          </div>
        </div>
        <div className={styles.actions}>
          <i className={cx(styles.addIcon, "paperflite-add")} />
        </div>
      </div>
    </div>
  );
};

export { AddAssetSlider, AssetSelection };
